import React, { useState } from 'react';

const CMC_MAX_DISPLAY = 7;

// Layout constants – tweak these to control the overall chart look.
const DEFAULT_MANA_CURVE_CHART_PADDING_X_PX = 5;
const DEFAULT_MANA_CURVE_BAR_GAP_PX = 7;
const DEFAULT_MANA_CURVE_BAR_MIN_HEIGHT_PX = 8;
const DEFAULT_MANA_CURVE_ZERO_TICK_HEIGHT_PX = 2;
const DEFAULT_MANA_CURVE_STATS_TO_CHART_FLEX_GAP_PX = 5;
const DEFAULT_MANA_CURVE_COUNT_AREA_HEIGHT_PX = 16;
const DEFAULT_MANA_CURVE_CMC_LABEL_TOP_GAP_PX = 4;
const DEFAULT_MANA_CURVE_CHART_BOTTOM_GAP_PX = 2;
const DEFAULT_MANA_CURVE_BASELINE_COLOR = 'var(--border)';
const DEFAULT_MANA_CURVE_LABEL_COLOR = 'var(--text-secondary)';

// Festes Pixelmaß statt einer Prozent-Kaskade (Balken füllen die tatsächlich
// verfügbare Resthöhe): verschachteltes `flex:1`/CSS-Grid-`1fr` in einer
// `auto`-hohen Umgebung lässt Chromium die Höhe iterativ aufblähen
// (reproduzierbar beobachtet: Container wuchs auf >9000px).
const DEFAULT_MANA_CURVE_BAR_MAX_HEIGHT_PX = 210;

export default function ManaCurve({
  cards,
  deckStats,
  onBarClick,
  highlightedCmc,
  barMaxHeight = DEFAULT_MANA_CURVE_BAR_MAX_HEIGHT_PX,
  chartPaddingXPx = DEFAULT_MANA_CURVE_CHART_PADDING_X_PX,
  barGapPx = DEFAULT_MANA_CURVE_BAR_GAP_PX,
  barMinHeightPx = DEFAULT_MANA_CURVE_BAR_MIN_HEIGHT_PX,
  zeroTickHeightPx = DEFAULT_MANA_CURVE_ZERO_TICK_HEIGHT_PX,
  statsToChartGapPx = DEFAULT_MANA_CURVE_STATS_TO_CHART_FLEX_GAP_PX,
  countAreaHeightPx = DEFAULT_MANA_CURVE_COUNT_AREA_HEIGHT_PX,
  cmcLabelTopGapPx = DEFAULT_MANA_CURVE_CMC_LABEL_TOP_GAP_PX,
  chartBottomGapPx = DEFAULT_MANA_CURVE_CHART_BOTTOM_GAP_PX,
}) {
  // Nutze zentrale deckStats wenn verfügbar, sonst interne Berechnung (Fallback)
  let cmcCounts;

  if (deckStats) {
    cmcCounts = deckStats.cmcCounts;
  } else {
    // Fallback: interne Berechnung (z.B. wenn ManaCurve ohne deckStats genutzt wird)
    cmcCounts = {};

    for (const card of cards) {
      const types = card.types || [];
      let isLand = types.some((t) => String(t).toLowerCase() === 'land');
      if (isLand) {
        isLand = card.imageUrlBack == null;
      }
      if (isLand) continue;

      const cmc = Math.min(Math.floor(card.cmc || 0), CMC_MAX_DISPLAY);
      const key = cmc >= CMC_MAX_DISPLAY ? (CMC_MAX_DISPLAY + '+') : String(cmc);
      cmcCounts[key] = (cmcCounts[key] || 0) + (card.quantity || 1);
    }
  }

  const [hoveredCmc, setHoveredCmc] = useState(null);

  const maxCount = Math.max(...Object.values(cmcCounts).filter((v) => typeof v === 'number'), 1);

  const getBarHeight = (count) => {
    if (!count) return zeroTickHeightPx;
    return Math.max(barMinHeightPx, (count / maxCount) * barMaxHeight);
  };

  const barAndCountAreaHeightPx = barMaxHeight + countAreaHeightPx;

  return (
    <div
      style={{
        height: '100%',
        width: '100%',
        maxWidth: 'none',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {/* Flexible gap so extra height goes *between* stats and bars */}
      <div style={{ flex: 1, minHeight: `${statsToChartGapPx}px` }} />

      <div style={{ padding: `0 ${chartPaddingXPx}px`, boxSizing: 'border-box' }}>
        {/* Bars + counts */}
        <div
          style={{
            height: `${barAndCountAreaHeightPx}px`,
            display: 'grid',
            gridTemplateColumns: `repeat(${CMC_MAX_DISPLAY + 1}, 1fr)`,
            gap: `${barGapPx}px`,
            alignItems: 'end',
          }}
        >
          {Array.from({ length: CMC_MAX_DISPLAY + 1 }, (_, i) => i).map((cmcValue) => {
            const key = cmcValue >= CMC_MAX_DISPLAY ? CMC_MAX_DISPLAY + '+' : String(cmcValue);
            const count = cmcCounts[key] || 0;
            const barHeight = getBarHeight(count);
            const isHighlighted = highlightedCmc === cmcValue;
            const isFilterActive = highlightedCmc !== null;
            const isHovered = hoveredCmc === cmcValue;
            const barOpacity = isHighlighted ? 1 : (isHovered ? 0.9 : 0.75);

            return (
              <div
                key={key}
                style={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  cursor: 'pointer',
                  opacity: isFilterActive && !isHighlighted ? 0.5 : 1,
                  transition: 'opacity 0.15s ease',
                  minWidth: 0,
                }}
                title={`${key}: ${count}`}
                onClick={() => onBarClick?.(cmcValue)}
                onMouseEnter={() => setHoveredCmc(cmcValue)}
                onMouseLeave={() => setHoveredCmc((prev) => (prev === cmcValue ? null : prev))}
              >
                <div
                  style={{
                    height: `${countAreaHeightPx}px`,
                    boxSizing: 'border-box',
                    fontSize: '0.7rem',
                    paddingBottom: '3px',
                    color: DEFAULT_MANA_CURVE_LABEL_COLOR,
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                  {count}
                </div>
                <div
                  style={{
                    width: '100%',
                    height: `${barHeight}px`,
                    background: 'var(--accent)',
                    opacity: barOpacity,
                    outline: isHighlighted ? '2px solid var(--accent)' : 'none',
                    outlineOffset: '-2px',
                    borderRadius: '4px 4px 0 0',
                    transition: 'opacity 0.15s ease',
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Baseline directly under the bars */}
        <div style={{ height: '1px', backgroundColor: DEFAULT_MANA_CURVE_BASELINE_COLOR }} />

        {/* CMC labels below baseline */}
        <div
          style={{
            marginTop: `${cmcLabelTopGapPx}px`,
            display: 'grid',
            gridTemplateColumns: `repeat(${CMC_MAX_DISPLAY + 1}, 1fr)`,
            gap: `${barGapPx}px`,
            alignItems: 'start',
          }}
        >
          {Array.from({ length: CMC_MAX_DISPLAY + 1 }, (_, i) => i).map((cmcValue) => {
            const key = cmcValue >= CMC_MAX_DISPLAY ? CMC_MAX_DISPLAY + '+' : String(cmcValue);
            return (
              <div
                key={key}
                style={{
                  textAlign: 'center',
                  fontSize: '0.7rem',
                  color: DEFAULT_MANA_CURVE_LABEL_COLOR,
                  userSelect: 'none',
                }}
              >
                {key}
              </div>
            );
          })}
        </div>

        <div style={{ height: `${chartBottomGapPx}px` }} />
      </div>
    </div>
  );
}
